import { type RefObject, useEffect, useRef } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Focus for a modal: moves in on open, cycles Tab inside, closes on Escape,
 * and hands focus back to whatever opened it. Without the cycle the first Tab
 * past the last button lands on the page behind the scrim.
 */
export function useDialogFocus(
  container: RefObject<HTMLElement | null>,
  onClose: () => void,
  { closable = true }: { closable?: boolean } = {},
) {
  const close = useRef(onClose)
  close.current = onClose
  const canClose = useRef(closable)
  canClose.current = closable

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const node = container.current
    if (node && !node.contains(document.activeElement)) node.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      const root = container.current
      if (!root) return
      if (event.key === 'Escape') {
        event.preventDefault()
        // Not while a request is under way: closing would hide its outcome.
        if (canClose.current) close.current()
        return
      }
      if (event.key !== 'Tab') return
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null,
      )
      const first = items[0]
      const last = items.at(-1)
      if (!first || !last) {
        event.preventDefault()
        root.focus()
        return
      }
      const active = document.activeElement
      // The focused element can unmount under the cursor (a step switch, a
      // retry button swapped for a list); focus then sits on <body> and the
      // next Tab would leave the dialog.
      if (!root.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }
      if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener?.isConnected) opener.focus()
    }
  }, [container])
}
